/**
 * LFO runtime state — the PWA-side mirror of OPBridge `lfo.state` snapshots.
 * Components read from here to drive UI (armed pulse, running glow, reported
 * value) without subscribing to a transport themselves.
 *
 * Mirrors `macros/runtime.ts` / `sequencer/runtime.ts` in shape. Written by
 * `opbridge/enginesStatePoll.ts` directly off `GET /engines/{consumer}/state`
 * — same direct-record pattern macro/sequencer already use, closing the
 * asymmetry where LFO state used to relay through the legacy bridge's
 * event-emitter (`bridge.__devEmit('lfo.state', ...)`) while macro/sequencer
 * never did.
 */

import { createSignal, type Accessor } from 'solid-js';
import type { LfoStateMessage } from '@opbuttons/schema';

export interface LfoRuntimeSnapshot {
  state: LfoStateMessage['state'];
  currentValue: number;
  /** Wall-clock ms this snapshot was received. */
  ts: number;
}

const IDLE: LfoRuntimeSnapshot = { state: 'idle', currentValue: 64, ts: 0 };

const [statesSignal, setStatesSignal] = createSignal<Record<string, LfoRuntimeSnapshot>>({});

export const lfoStates: Accessor<Record<string, LfoRuntimeSnapshot>> = statesSignal;

/** Called by `enginesStatePoll.ts` for every LFO in each poll snapshot. */
export function recordLfoState(msg: LfoStateMessage): void {
  setStatesSignal((prev) => ({
    ...prev,
    [msg.lfoId]: {
      state: msg.state,
      currentValue: msg.currentValue,
      ts: Date.now(),
    },
  }));
}

/** An LFO OPBridge no longer lists has stopped (a stop, PANIC, a one-shot that
 *  ended): the engine drops a stopped runtime from its snapshot, so without
 *  this the tile latched on its last running state (2026-11-03 audit A-5). */
export function recordLfoIdle(lfoId: string): void {
  setStatesSignal((prev) => ({ ...prev, [lfoId]: { state: 'idle', currentValue: 64, ts: Date.now() } }));
}

/** Best-effort accessor for a specific LFO's state. Returns the idle default
 *  when nothing has been reported for this id yet (never armed, or OPBridge
 *  hasn't been polled since boot). */
export function lfoState(lfoId: string): LfoRuntimeSnapshot {
  return statesSignal()[lfoId] ?? IDLE;
}
